import React, { useState, useEffect } from 'react';
import { Download, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { trackPWAInstallPromptShown, trackPWAInstallAccepted, trackPWAInstallDismissed, trackPWALaunched } from '../utils/analytics';
import axios from 'axios';

const API = '/api';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone || 
                      document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);

    // If already installed, store flag in localStorage
    if (standalone && !localStorage.getItem('pwa_installed')) {
      localStorage.setItem('pwa_installed', 'true');
      trackPWALaunched();
      toast.success('🎉 Welcome to HQSTUDIO+! Get 10% off when you make your first booking through the app!', {
        duration: 8000
      });
    }

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissed) {
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a delay if not installed
      if (!standalone) {
        setTimeout(() => {
          if (window.location.pathname.startsWith('/book') || window.location.search.includes('payment=') || sessionStorage.getItem('hq_booking_active')) return;
          setShowPrompt(true);
          trackPWAInstallPromptShown();
        }, 3000); // Show after 3 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    const handleAppInstalled = async () => {
      console.log('PWA was installed');
      setShowPrompt(false);
      setDeferredPrompt(null);
      localStorage.setItem('pwa_installed', 'true');
      trackPWAInstallAccepted();
      
      // Notify backend about PWA installation
      try {
        const userEmail = localStorage.getItem('user_email'); // Try to get user email if available
        if (userEmail) {
          await axios.post(`${API}/pwa-installed`, { email: userEmail });
          console.log('PWA installation notification sent to backend');
        }
      } catch (error) {
        console.error('Error notifying backend about PWA installation:', error);
      }
      
      toast.success('🎉 HQSTUDIO+ installed! Get 10% off when you make your first booking through the app!', {
        duration: 8000
      });
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setShowPrompt(false);
      localStorage.setItem('pwa_installed', 'true');
      
      // Notify backend about PWA installation
      try {
        const userEmail = localStorage.getItem('user_email'); // Try to get user email if available
        if (userEmail) {
          await axios.post(`${API}/pwa-installed`, { email: userEmail });
          console.log('PWA installation notification sent to backend');
        }
      } catch (error) {
        console.error('Error notifying backend about PWA installation:', error);
      }
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed', 'true');
    trackPWAInstallDismissed();
  };

  // Don't show if already installed or dismissed
  if (!showPrompt || isStandalone) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-[9999] animate-slide-up">
      <div className="bg-gradient-to-br from-purple-900 to-pink-900 backdrop-blur-sm border-2 border-purple-500/50 rounded-2xl p-6 shadow-2xl">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
          aria-label="Dismiss"
        >
          <X size={20} />
        </button>

        <div className="flex items-start space-x-4">
          <div className="bg-purple-600 p-3 rounded-full">
            <Download size={24} className="text-white" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-lg font-bold text-white">Install HQSTUDIO+</h3>
              <Sparkles size={16} className="text-yellow-400" />
            </div>
            
            <p className="text-gray-200 text-sm mb-3">
              Install now and get <span className="font-bold text-yellow-400">10% off your first booking</span> when you book through the app!
            </p>

            <div className="flex space-x-2">
              <button
                onClick={handleInstallClick}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg font-semibold hover:scale-105 transition-transform text-sm"
              >
                Install Now
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors text-sm"
              >
                Later
              </button>
            </div>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-purple-500/30">
          <ul className="text-xs text-gray-300 space-y-1">
            <li>✓ Faster loading times</li>
            <li>✓ Works offline</li>
            <li>✓ Home screen access</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
